import { Fragment } from 'react';
import type { ReactNode } from 'react';

// Minimal Obsidian-flavoured markdown → React renderer used by the daily-note
// live-preview editor. It renders one source line at a time (the editor swaps
// the cursor's line back to raw text), so block handling here is per-line:
// headings, quotes, lists, tasks, rules. Inline handling (bold/italic/code/
// links/wikilinks/tags/highlight/strike) runs inside each block.

// ---- inline ----

interface InlineRule {
  re: RegExp;
  render: (m: RegExpMatchArray, key: number) => ReactNode;
}

// Wrap a list of nodes with stable keys so React doesn't warn about array
// children. Plain strings and elements both pass through.
function frag(nodes: ReactNode[]): ReactNode {
  return nodes.map((n, i) => <Fragment key={i}>{n}</Fragment>);
}

// Non-global regexes: `exec` returns the first (earliest) match, which is what
// the earliest-wins scan in `parseInline` relies on. Order matters only for
// ties at the same index — code first so backtick spans win.
const INLINE_RULES: InlineRule[] = [
  {
    // inline code — no nesting inside
    re: /`([^`]+)`/,
    render: (m, key) => (
      <code
        key={key}
        className="rounded bg-[var(--bg-hover)] px-1 py-[1px] font-mono text-[0.85em] text-[var(--text)]"
      >
        {m[1]}
      </code>
    )
  },
  {
    re: /\*\*([^*]+?)\*\*/,
    render: (m, key) => <strong key={key}>{frag(parseInline(m[1]))}</strong>
  },
  {
    re: /__([^_]+?)__/,
    render: (m, key) => <strong key={key}>{frag(parseInline(m[1]))}</strong>
  },
  {
    re: /\*([^*\n]+?)\*/,
    render: (m, key) => <em key={key}>{frag(parseInline(m[1]))}</em>
  },
  {
    re: /(?<![A-Za-z0-9])_([^_\n]+?)_(?![A-Za-z0-9])/,
    render: (m, key) => <em key={key}>{frag(parseInline(m[1]))}</em>
  },
  {
    re: /~~([^~]+?)~~/,
    render: (m, key) => (
      <del key={key} className="text-[var(--text-muted)]">
        {frag(parseInline(m[1]))}
      </del>
    )
  },
  {
    re: /==([^=]+?)==/,
    render: (m, key) => (
      <mark key={key} className="rounded bg-[var(--today-bg)] px-[2px] text-[var(--text)]">
        {frag(parseInline(m[1]))}
      </mark>
    )
  },
  {
    // [[wikilink]] or [[target|alias]]
    re: /\[\[([^\]]+?)\]\]/,
    render: (m, key) => {
      const [target, alias] = m[1].split('|');
      return (
        <span key={key} className="text-[var(--accent)] underline decoration-dotted">
          {alias ?? target}
        </span>
      );
    }
  },
  {
    // [text](url)
    re: /\[([^\]]+?)\]\(([^)\s]+)\)/,
    render: (m, key) => (
      <a
        key={key}
        href={m[2]}
        target="_blank"
        rel="noreferrer"
        className="text-[var(--accent)] underline"
        onClick={(e) => e.stopPropagation()}
      >
        {frag(parseInline(m[1]))}
      </a>
    )
  },
  {
    // #tag — must be preceded by start or whitespace (the capture re-emits it)
    re: /(^|\s)#([A-Za-z0-9_][\w/-]*)/,
    render: (m, key) => (
      <Fragment key={key}>
        {m[1]}
        <span className="rounded bg-[var(--bg-hover)] px-[5px] text-[0.85em] text-[var(--accent)]">
          #{m[2]}
        </span>
      </Fragment>
    )
  }
];

function parseInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  while (rest.length) {
    let best: { rule: InlineRule; m: RegExpMatchArray } | null = null;
    for (const rule of INLINE_RULES) {
      const m = rule.re.exec(rest);
      if (m && (best === null || m.index! < best.m.index!)) best = { rule, m };
    }
    if (!best) {
      out.push(rest);
      break;
    }
    const { rule, m } = best;
    if (m.index! > 0) out.push(rest.slice(0, m.index));
    out.push(rule.render(m, out.length));
    rest = rest.slice(m.index! + m[0].length);
  }
  return out;
}

// ---- block (per line) ----

const HEADING_CLASS = [
  'text-[1.5rem] font-bold',
  'text-[1.3rem] font-bold',
  'text-[1.15rem] font-bold',
  'text-[1.05rem] font-semibold',
  'text-[0.95rem] font-semibold',
  'text-[0.9rem] font-semibold uppercase tracking-wide text-[var(--text-muted)]'
];

// Tailwind utility classes to apply to the *raw* textarea when its line is one
// of these block types, so editing a heading keeps the heading's size (the
// `# ` markup just becomes visible). Mirrors the rendered sizes above.
export function activeLineClass(line: string): string {
  const h = /^(#{1,6})\s/.exec(line);
  if (h) return HEADING_CLASS[h[1].length - 1];
  return '';
}

// Inline markdown → React, exported for callout titles / property values.
export function renderInlineNodes(text: string): ReactNode {
  return frag(parseInline(text));
}

export function renderLine(line: string): ReactNode {
  // headings
  const h = /^(#{1,6})\s+(.*)$/.exec(line);
  if (h) {
    const level = h[1].length;
    return <span className={HEADING_CLASS[level - 1]}>{frag(parseInline(h[2]))}</span>;
  }

  // horizontal rule
  if (/^\s*(---|\*\*\*|___)\s*$/.test(line)) {
    return <span className="block border-t border-[var(--border-strong)]" aria-hidden />;
  }

  // blockquote
  const q = /^>\s?(.*)$/.exec(line);
  if (q) {
    return (
      <span className="block border-l-2 border-[var(--border-strong)] pl-2 text-[var(--text-muted)]">
        {frag(parseInline(q[1]))}
      </span>
    );
  }

  // task list
  const task = /^(\s*)[-*+]\s+\[([ xX])\]\s+(.*)$/.exec(line);
  if (task) {
    const indent = task[1].length;
    const done = task[2] !== ' ';
    return (
      <span className="flex items-baseline gap-2" style={{ paddingLeft: indent * 0.9 + 'rem' }}>
        <input type="checkbox" checked={done} readOnly className="translate-y-[2px]" tabIndex={-1} />
        <span className={done ? 'text-[var(--text-faint)] line-through' : undefined}>
          {frag(parseInline(task[3]))}
        </span>
      </span>
    );
  }

  // ordered list
  const ol = /^(\s*)(\d+)\.\s+(.*)$/.exec(line);
  if (ol) {
    const indent = ol[1].length;
    return (
      <span className="flex gap-2" style={{ paddingLeft: indent * 0.9 + 'rem' }}>
        <span className="text-[var(--text-muted)]">{ol[2]}.</span>
        <span>{frag(parseInline(ol[3]))}</span>
      </span>
    );
  }

  // unordered list
  const ul = /^(\s*)[-*+]\s+(.*)$/.exec(line);
  if (ul) {
    const indent = ul[1].length;
    return (
      <span className="flex gap-2" style={{ paddingLeft: indent * 0.9 + 'rem' }}>
        <span className="text-[var(--text-muted)]">•</span>
        <span>{frag(parseInline(ul[2]))}</span>
      </span>
    );
  }

  // blank line — keep a line-box so the rendered run lines up with editing
  if (line.trim() === '') return <span className="block">{' '}</span>;

  // paragraph
  return <span>{frag(parseInline(line))}</span>;
}
